import { Link } from "react-router-dom";
import { CalendarClock, ClipboardList, ReceiptText, ShieldCheck, ChevronRight, Check, FileText } from "lucide-react";

/**
 * Guided workflow strip for the Estimate → Appointment → Job Card → Invoice → Gate Pass flow.
 *
 * Props:
 *   currentStep: "estimate" | "appointment" | "job_card" | "invoice" | "gate_pass"
 *   estimate:   optional { id, number } — if present, marked done
 *   appointment: optional { id, number }
 *   jobCard:     optional { id, number, status }
 *   invoice:     optional { id, number, status, balance }
 *   gatePass:    optional { id, number, status }
 *   nextAction:  optional { label, to, testId, disabled?, tooltip? }
 *   blockReason: optional string — explains why nextAction is unavailable (yellow banner)
 */
const STEPS = [
  { key: "estimate",    label: "Estimate",    Icon: FileText,      color: "#457b9d" },
  { key: "appointment", label: "Appointment", Icon: CalendarClock, color: "#457b9d" },
  { key: "job_card",    label: "Job Card",    Icon: ClipboardList, color: "#e63946" },
  { key: "invoice",     label: "Invoice",     Icon: ReceiptText,   color: "#1d3557" },
  { key: "gate_pass",   label: "Gate Pass",   Icon: ShieldCheck,   color: "#12B76A" },
];

export default function WorkflowStrip({
  currentStep, estimate, appointment, jobCard, invoice, gatePass,
  nextAction, blockReason,
}) {
  const links = {
    estimate:    estimate?.id    ? `/estimates/${estimate.id}` : null,
    appointment: appointment?.id ? `/appointments/${appointment.id}` : null,
    job_card:    jobCard?.id     ? `/job-cards/${jobCard.id}` : null,
    invoice:     invoice?.id     ? `/invoices/${invoice.id}` : null,
    gate_pass:   gatePass?.id    ? `/gate-pass/${gatePass.id}` : null,
  };
  const nums = {
    estimate:    estimate?.number,
    appointment: appointment?.number,
    job_card:    jobCard?.number || jobCard?.job_number,
    invoice:     invoice?.number,
    gate_pass:   gatePass?.number,
  };

  const currentIdx = STEPS.findIndex((s) => s.key === currentStep);

  return (
    <div className="rounded-xl border border-[#E4E7EC] bg-white p-3 md:p-4 mb-4" data-testid="workflow-strip">
      {/* Step chain */}
      <div className="flex items-center gap-1 md:gap-2 overflow-x-auto pb-1">
        {STEPS.map((s, idx) => {
          const isCurrent = s.key === currentStep;
          const linkTo = links[s.key];
          const done = !!linkTo && idx < currentIdx;
          const pending = idx > currentIdx && !linkTo;
          const Icon = s.Icon;
          const chipCls = isCurrent
            ? "bg-[#1d3557] text-white ring-2 ring-[#1d3557]/40"
            : done
              ? "bg-[#12B76A]/12 text-[#0F9256] border border-[#12B76A]/30"
              : linkTo
                ? "bg-white text-[#1d3557] border border-[#E4E7EC]"
                : "bg-[#F7F9FC] text-[#a8dadc] border border-[#E4E7EC]";

          const inner = (
            <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11.5px] font-semibold whitespace-nowrap ${chipCls}`}
                 data-testid={`workflow-step-${s.key}${isCurrent ? "-current" : done ? "-done" : ""}`}>
              {done ? <Check size={11} /> : <Icon size={11} />}
              <span className="hidden sm:inline">{s.label}</span>
              {nums[s.key] && (
                <span className="font-mono text-[10px] opacity-80 hidden md:inline">
                  {nums[s.key]}
                </span>
              )}
              {pending && <span className="hidden md:inline text-[9.5px] uppercase tracking-wider opacity-60">Pending</span>}
            </div>
          );
          return (
            <div key={s.key} className="flex items-center gap-1 md:gap-2">
              {linkTo && !isCurrent ? (
                <Link to={linkTo} data-testid={`workflow-link-${s.key}`}>{inner}</Link>
              ) : (
                inner
              )}
              {idx < STEPS.length - 1 && (
                <ChevronRight size={12} className="text-[#a8dadc] shrink-0" />
              )}
            </div>
          );
        })}
      </div>

      {/* Next action CTA */}
      {nextAction && (
        <div className="mt-3 flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3 rounded-lg bg-[#F1FAEE] border border-[#a8dadc]/40 px-3 py-2.5">
          <div className="flex-1">
            <div className="text-[10.5px] uppercase tracking-wider font-bold text-[#457b9d]">Next step</div>
            <div className="text-[13px] font-semibold text-[#1d3557]">{nextAction.hint || nextAction.label}</div>
          </div>
          {nextAction.disabled ? (
            <button type="button" disabled title={nextAction.tooltip}
                    data-testid={nextAction.testId || "workflow-next-action-disabled"}
                    className="h-10 px-4 rounded-md bg-[#a8dadc]/40 text-[#667085] text-[12.5px] font-semibold cursor-not-allowed inline-flex items-center gap-1.5">
              {nextAction.label} <ChevronRight size={13} />
            </button>
          ) : (
            <Link to={nextAction.to}
                  data-testid={nextAction.testId || "workflow-next-action"}
                  className="h-10 px-4 rounded-md bg-[#e63946] hover:bg-[#d02f3c] text-white text-[12.5px] font-semibold inline-flex items-center gap-1.5">
              {nextAction.label} <ChevronRight size={13} />
            </Link>
          )}
        </div>
      )}

      {blockReason && !nextAction && (
        <div className="mt-3 rounded-lg bg-[#FEF0C7] border border-[#F79009]/40 px-3 py-2 text-[12px] text-[#B54708]"
             data-testid="workflow-block-reason">
          {blockReason}
        </div>
      )}
    </div>
  );
}
